import React, { useState } from 'react';
import { useApi } from '../hooks/useApi';
import { useOnSyncComplete } from '../hooks/useSyncStatus';
import { api } from '../api';
import { fmt, fmtDate, extractCategory } from '../utils';
import {
  Card, CardHeader, TwoCol,
  Badge, Skeleton, PageWrap, PageHeader, Table, useIsMobile,
} from '../components/UI';
import { AUDIT_STATUS_CONFIG } from '../auditStatus';
import '../styles/Overview.css';

function AuditStatusBadge({ status }) {
  const cfg = AUDIT_STATUS_CONFIG[status || 'synced'] || AUDIT_STATUS_CONFIG.synced;
  return (
    <span className="ov-status-badge" style={{ background: cfg.bg, color: cfg.color, borderColor: `${cfg.color}33` }}>
      {cfg.label}
    </span>
  );
}

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

function getAuthHeaders() {
  try {
    const saved = localStorage.getItem('auditpros_user');
    if (saved) {
      const parsed  = JSON.parse(saved);
      const account = parsed?.account;
      const email   = account?.idTokenClaims?.email
        || account?.idTokenClaims?.preferred_username
        || account?.idTokenClaims?.emails?.[0]
        || account?.username || account?.preferred_username || account?.email;
      const viewMode = localStorage.getItem('auditpros_view_mode') || 'ca';
      const headers  = {};
      if (email) headers['x-user-email'] = email;
      headers['x-view-mode'] = viewMode;
      return headers;
    }
  } catch { /* ignore */ }
  return {};
}

// ── Small shared helpers ──────────────────────────────────────────────────────

function SectionLabel({ children }) {
  return (
    <div className="ov-section-label">
      <span>{children}</span>
      <div className="ov-section-label-line" />
    </div>
  );
}

function TotalPill({ label, value, color = 'var(--text2)' }) {
  return (
    <span className="ov-total-pill" style={{ color }}>
      {label}: {value}
    </span>
  );
}

// ── Monthly Sales vs Expenditure bar chart ────────────────────────────────────
function MonthlyChart({ salesRows, purchRows, loading }) {
  const isMobile = useIsMobile();

  const fmt2 = (v) => {
    const abs = Math.abs(v);
    if (abs >= 10000000) return `₹${(abs / 10000000).toFixed(1)}Cr`;
    if (abs >= 100000)   return `₹${(abs / 100000).toFixed(1)}L`;
    if (abs >= 1000)     return `₹${(abs / 1000).toFixed(0)}K`;
    return `₹${abs.toFixed(0)}`;
  };

  const groupByMonth = (rows) => {
    const map = {};
    rows.forEach(r => {
      const d = r.date || r.invoice_date || '';
      if (!d) return;
      let month;
      if (/^\d{8}$/.test(d)) {
        month = `${d.slice(0, 4)}-${d.slice(4, 6)}`;
      } else {
        const parsed = new Date(d);
        if (isNaN(parsed)) return;
        month = `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}`;
      }
      map[month] = (map[month] || 0) + parseFloat(r.invoice_amount || 0);
    });
    return map;
  };

  const salesByMonth = groupByMonth(salesRows);
  const purchByMonth = groupByMonth(purchRows);

  const allMonths = [...new Set([
    ...Object.keys(salesByMonth),
    ...Object.keys(purchByMonth),
  ])].sort();

  if (allMonths.length === 0) {
    return (
      <Card className="ov-card-mb">
        <CardHeader title="Monthly Sales vs Expenditure" />
        {loading ? <Skeleton rows={3} /> : <div className="ov-empty-msg">No monthly data yet.</div>}
      </Card>
    );
  }

  const months = allMonths.map(m => {
    const [yr, mo] = m.split('-');
    const sales = salesByMonth[m] || 0;
    const purch = purchByMonth[m] || 0;
    const label = new Date(Number(yr), Number(mo) - 1, 1)
      .toLocaleDateString('en-IN', { month: 'short', year: '2-digit' });
    return { key: m, label, sales, purch };
  });

  const maxVal = Math.max(...months.map(m => Math.max(m.sales, m.purch)), 1);

  // ── Mobile: horizontal bar list ──────────────────────────────────────────
  if (isMobile) {
    return (
      <Card className="ov-card-mb">
        <CardHeader
          title="Monthly Sales vs Expenditure"
          right={
            <div className="ov-legend">
              {[['#34d399', 'Sales'], ['#f87171', 'Exp']].map(([c, l]) => (
                <div key={l} className="ov-legend-item">
                  <div className="ov-legend-dot" style={{ background: c }} />
                  <span className="ov-legend-label">{l}</span>
                </div>
              ))}
            </div>
          }
        />
        {loading ? <Skeleton rows={4} /> : (
          <div className="ov-mobile-chart-body">
            {months.map(m => {
              const net = m.sales - m.purch;
              const positive = net >= 0;
              const salesW = (m.sales / maxVal) * 100;
              const purchW = (m.purch / maxVal) * 100;
              return (
                <div key={m.key}>
                  <div className="ov-mobile-month-header">
                    <span className="ov-mobile-month-label">{m.label}</span>
                    <span style={{ fontSize: 11, fontWeight: 700, fontFamily: 'var(--mono)', color: positive ? '#34d399' : '#f87171' }}>
                      {positive ? '+' : ''}{fmt2(net)}
                    </span>
                  </div>
                  <div className="ov-bar-track">
                    <div style={{ height: '100%', width: `${salesW}%`, background: 'linear-gradient(90deg,#059669,#34d399)', borderRadius: 4, transition: 'width 0.6s ease' }} />
                  </div>
                  <div className="ov-bar-track" style={{ marginTop: 3 }}>
                    <div style={{ height: '100%', width: `${purchW}%`, background: 'linear-gradient(90deg,#dc2626,#f87171)', borderRadius: 4, transition: 'width 0.6s ease' }} />
                  </div>
                  <div className="ov-mobile-bar-footer">
                    <span style={{ color: '#34d399' }}>{fmt2(m.sales)}</span>
                    <span style={{ color: '#f87171' }}>{fmt2(m.purch)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    );
  }

  // ── Desktop: HTML/CSS chart ───────────────────────────────────────────────
  const CHART_H  = 160;
  const Y_AXIS_W = 48;

  const gridLevels = [0.25, 0.5, 0.75, 1.0].map(f => ({
    val: maxVal * f,
    pct: f * 100,
  }));

  return (
    <Card className="ov-card-mb">
      <CardHeader
        title="Monthly Sales vs Expenditure"
        right={
          <div className="ov-legend-desktop">
            {[['#34d399', 'Sales'], ['#f87171', 'Expenditure']].map(([c, l]) => (
              <div key={l} className="ov-legend-item-desktop">
                <div className="ov-legend-dot-desktop" style={{ background: c }} />
                <span className="ov-legend-label">{l}</span>
              </div>
            ))}
          </div>
        }
      />
      {loading ? <Skeleton rows={4} /> : (
        <div className="ov-chart-body">
          <div className="ov-chart-inner">
            {/* Y-axis labels */}
            <div style={{ width: Y_AXIS_W, flexShrink: 0, height: CHART_H, position: 'relative' }}>
              {gridLevels.map(({ val, pct }) => (
                <div key={pct} style={{
                  position: 'absolute', bottom: `${pct}%`, right: 6,
                  transform: 'translateY(50%)',
                  fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)',
                  whiteSpace: 'nowrap', lineHeight: 1,
                }}>
                  {fmt2(val)}
                </div>
              ))}
            </div>

            {/* Bar plot area */}
            <div className="ov-chart-plot-area">
              {/* Gridlines + bars */}
              <div style={{ position: 'relative', height: CHART_H, flex: 'none' }}>
                {gridLevels.map(({ pct }) => (
                  <div key={pct} style={{
                    position: 'absolute', bottom: `${pct}%`, left: 0, right: 0,
                    height: 1, background: 'var(--border)', opacity: 0.6,
                  }} />
                ))}
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 1, background: 'var(--border)' }} />
                <div style={{ display: 'flex', height: '100%', alignItems: 'flex-end' }}>
                  {months.map((m) => {
                    const salesH = (m.sales / maxVal) * 100;
                    const purchH = (m.purch / maxVal) * 100;
                    return (
                      <div key={m.key} className="ov-chart-bar-col">
                        <div title={`${m.label} Sales: ${fmt2(m.sales)}`} style={{
                          width: 'clamp(6px, 1.8vw, 22px)',
                          height: `${Math.max(salesH, m.sales > 0 ? 1 : 0)}%`,
                          background: 'linear-gradient(180deg, #34d399, #059669)',
                          borderRadius: '3px 3px 0 0', opacity: 0.92,
                          transition: 'height 0.6s ease', flexShrink: 0,
                        }} />
                        <div title={`${m.label} Expenditure: ${fmt2(m.purch)}`} style={{
                          width: 'clamp(6px, 1.8vw, 22px)',
                          height: `${Math.max(purchH, m.purch > 0 ? 1 : 0)}%`,
                          background: 'linear-gradient(180deg, #f87171, #dc2626)',
                          borderRadius: '3px 3px 0 0', opacity: 0.92,
                          transition: 'height 0.6s ease', flexShrink: 0,
                        }} />
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* X-axis labels */}
              <div className="ov-chart-x-axis">
                {months.map((m) => {
                  const net = m.sales - m.purch;
                  const positive = net >= 0;
                  return (
                    <div key={m.key} className="ov-chart-x-item">
                      <span className="ov-chart-month-label">{m.label}</span>
                      <span style={{ fontSize: 10, fontWeight: 700, fontFamily: 'var(--mono)', color: positive ? '#34d399' : '#f87171', whiteSpace: 'nowrap' }}>
                        {positive ? '+' : ''}{fmt2(net)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

// ── Balance sheet check mini-card ─────────────────────────────────────────────
function BalanceCheckCard({ data, loading }) {
  const entries = data?.unbalancedEntries ?? data?.unbalanced_entries ?? [];
  const count   = data?.unbalancedCount   ?? data?.unbalanced_count  ?? entries.length;
  const ok      = data?.isFullyBalanced   ?? (count === 0);

  return (
    <Card>
      <CardHeader
        title="Balance Sheet Check"
        right={data != null ? <Badge text={ok ? 'BALANCED' : `${count} unbalanced`} variant={ok ? 'green' : 'red'} /> : null}
      />
      {loading ? <Skeleton rows={3} /> : data == null ? (
        <div className="ov-empty-msg">No data — run a sync first.</div>
      ) : (
        <div className="ov-balance-body">
          <div
            className={`ov-balance-status ov-balance-status--${ok ? 'ok' : 'err'}`}
            style={{ marginBottom: entries.length > 0 ? 12 : 0 }}
          >
            <span className="ov-balance-icon">{ok ? '✓' : '✗'}</span>
            <div>
              <div className={`ov-balance-message ov-balance-message--${ok ? 'ok' : 'err'}`}>
                {ok ? 'All vouchers are balanced' : `${count} unbalanced voucher${count > 1 ? 's' : ''} found`}
              </div>
              <div className="ov-balance-note">
                {ok
                  ? 'Every transaction has equal debits and credits.'
                  : 'Entries with mismatched debits and credits — must be corrected in Tally.'}
              </div>
            </div>
          </div>
          {entries.length > 0 && (
            <Table
              columns={[
                { label: 'Date', key: 'date', noWrap: true,
                  render: r => <span className="ov-text-secondary">{fmtDate(r.date)}</span> },
                { label: 'Voucher', key: 'voucher_number',
                  render: r => <span className="ov-mono-accent">{r.voucher_number ?? r.voucherNumber}</span> },
                { label: 'Party', key: 'party',
                  render: r => <span className="ov-ledger-name">{r.party ?? r.raw_party ?? '—'}</span> },
                { label: 'Discrepancy (₹)', key: 'discrepancy', right: true, mono: true,
                  render: r => {
                    const v = parseFloat(r.discrepancy ?? r.entry_sum ?? 0);
                    return v
                      ? <span className="ov-mono-red ov-bold">₹{fmt(Math.abs(v))}</span>
                      : <span className="ov-text-dim">—</span>;
                  }},
              ]}
              rows={entries.slice(0, 5)}
              emptyMsg="All balanced"
            />
          )}
        </div>
      )}
    </Card>
  );
}

// ── Trial balance mini-card ───────────────────────────────────────────────────
function TrialBalanceCard({ data, loading }) {
  const balanced    = data?.balanced;
  const debitTotal  = data?.debitTotal  ?? data?.grandTotal ?? 0;
  const creditTotal = data?.creditTotal ?? data?.grandTotal ?? 0;
  const ledgerCount = data?.ledgers?.length ?? 0;

  const top5 = (data?.ledgers || [])
    .filter(x => Math.abs(parseFloat(x.balance ?? 0)) > 0.01)
    .slice()
    .sort((a, b) => Math.abs(parseFloat(b.balance ?? 0)) - Math.abs(parseFloat(a.balance ?? 0)))
    .slice(0, 5);

  const stats = [
    ['Dr Total', `₹${fmt(debitTotal)}`,  'var(--red)'],
    ['Cr Total', `₹${fmt(creditTotal)}`, 'var(--green)'],
    ['Ledgers',  ledgerCount,             'var(--text)'],
  ];

  return (
    <Card>
      <CardHeader
        title="Trial Balance"
        right={balanced != null ? <Badge text={balanced ? 'BALANCED' : 'UNBALANCED'} variant={balanced ? 'green' : 'red'} /> : null}
      />
      {loading ? <Skeleton rows={4} /> : !data ? (
        <div className="ov-empty-msg">No data — run a sync first.</div>
      ) : (
        <div>
          <div className="ov-stat-grid-3">
            {stats.map(([label, value, color]) => (
              <div key={label} className="ov-stat-cell">
                <div className="ov-stat-label">{label}</div>
                <div className="ov-stat-value" style={{ color }}>{value}</div>
              </div>
            ))}
          </div>
          <div className="ov-sublabel">Top balances</div>
          <Table
            columns={[
              { label: 'Ledger', key: 'ledger',
                render: r => <span className="ov-ledger-name">{r.ledger ?? r.raw_ledger_name ?? '—'}</span> },
              { label: 'Balance (₹)', key: 'balance', right: true, mono: true,
                render: r => {
                  const b = parseFloat(r.balance ?? 0);
                  const side = r.side ?? (b < 0 ? 'Dr' : 'Cr');
                  return <span className={`${side === 'Dr' ? 'ov-mono-red' : 'ov-mono-green'} ov-bold`}>₹{fmt(Math.abs(b))}</span>;
                }},
              { label: 'Side', key: 'side',
                render: r => {
                  const b = parseFloat(r.balance ?? 0);
                  const side = r.side ?? (b < 0 ? 'Dr' : 'Cr');
                  return <Badge text={side} variant={side === 'Dr' ? 'red' : 'green'} />;
                }},
            ]}
            rows={top5}
            emptyMsg="No ledger data"
          />
        </div>
      )}
    </Card>
  );
}

// ── Sales vs Expenditure P&L waterfall ───────────────────────────────────────
function SalesVsExpenditure({ totalSales, totalSalesTax, totalPurchases, totalITC, totalUnbooked, indirectExpense, indirectIncome, salesRowsLen, loading }) {
  const grossRevenue   = totalSales;
  const netRevenue     = totalSales - totalSalesTax;
  const netPurchases   = totalPurchases - totalITC;
  const grossProfit    = netRevenue - netPurchases;
  // Indirect Expenses/Incomes come from actual ledger closing balances
  // (Trial Balance), matching how Tally itself computes Net Profit —
  // NOT Unbooked Payments or Statutory Dues, which are compliance/
  // reconciliation signals, not P&L expenses, and previously produced
  // wildly wrong "profit" figures (e.g. a multi-crore loss shown for a
  // company that was actually profitable per Tally).
  const netProfit   = grossProfit - indirectExpense + indirectIncome;
  const profitable  = netProfit >= 0;

  const maxBar      = Math.max(grossRevenue, totalPurchases + totalUnbooked, 1);
  const salesPct    = (grossRevenue / maxBar) * 100;
  const purchPct    = (totalPurchases / maxBar) * 100;
  const unbookedPct = (totalUnbooked  / maxBar) * 100;

  const fmt2 = (v) => {
    const abs = Math.abs(v);
    const sign = v < 0 ? '-' : '';
    if (abs >= 10000000) return `${sign}₹${(abs / 10000000).toFixed(2)} Cr`;
    if (abs >= 100000)   return `${sign}₹${(abs / 100000).toFixed(2)} L`;
    if (abs >= 1000)     return `${sign}₹${(abs / 1000).toFixed(1)} K`;
    return `${sign}₹${abs.toFixed(0)}`;
  };

  const pct = (part, whole) => whole > 0 ? ((part / whole) * 100).toFixed(1) + '%' : '—';

  const waterfall = [
    { label: 'Gross Revenue',        value: grossRevenue,       sub: `${salesRowsLen} invoices`,                              color: '#34d399', deduction: false },
    { label: 'Less: GST on Sales',   value: -totalSalesTax,     sub: `collected, not income`,                                 color: '#94a3b8', deduction: true  },
    { label: 'Net Revenue',          value: netRevenue,         sub: null,                                                    color: '#6ee7b7', deduction: false, separator: true },
    { label: 'Less: Purchases',       value: -netPurchases,      sub: `gross ${fmt2(totalPurchases)} − ITC ${fmt2(totalITC)}`, color: '#f87171', deduction: true  },
    { label: 'Gross Profit',          value: grossProfit,        sub: null,                                                    color: '#6ee7b7', deduction: false, separator: true },
    { label: 'Less: Indirect Expenses', value: -indirectExpense, sub: `rent, salaries, admin & other overheads`,               color: '#f87171', deduction: true  },
    { label: 'Add: Indirect Incomes', value: indirectIncome,     sub: `interest, misc. income, etc.`,                          color: '#34d399', deduction: false },
    { label: 'Estimated Profit',      value: netProfit,          sub: `${pct(netProfit, grossRevenue)} margin`,                color: profitable ? '#34d399' : '#f87171', deduction: false, final: true },
  ];

  return (
    <Card className="ov-card-mb">
      <CardHeader
        title="P&L Summary"
        right={
          <span
            className="ov-pnl-profit-badge"
            style={{
              background: profitable ? 'rgba(52,211,153,0.12)' : 'rgba(248,113,113,0.12)',
              color: profitable ? '#34d399' : '#f87171',
              borderColor: profitable ? 'rgba(52,211,153,0.3)' : 'rgba(248,113,113,0.3)',
            }}
          >
            Est. Profit: {fmt2(netProfit)}
          </span>
        }
      />
      {loading ? <Skeleton rows={4} /> : (
        <div className="ov-pnl-body">
          <div className="ov-pnl-bars">
            {/* Sales bar */}
            <div>
              <div className="ov-pnl-bar-header">
                <span className="ov-pnl-label-sales">Total Sales (Gross)</span>
                <span className="ov-pnl-amt-sales">{fmt2(grossRevenue)}</span>
              </div>
              <div className="ov-pnl-bar-26">
                <div style={{
                  height: '100%', width: `${salesPct}%`,
                  background: 'linear-gradient(90deg, #059669, #34d399)',
                  borderRadius: 6, transition: 'width 0.8s cubic-bezier(0.4,0,0.2,1)',
                  display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: 8,
                }}>
                  {salesPct > 16 && <span style={{ fontSize: 11, fontWeight: 700, color: '#ecfdf5' }}>{salesPct.toFixed(0)}%</span>}
                </div>
              </div>
            </div>
            {/* Expenditure bar */}
            <div>
              <div className="ov-pnl-bar-header">
                <div className="ov-pnl-exp-sub">
                  <span className="ov-pnl-label-exp">Total Expenditure</span>
                  {totalUnbooked > 0 && (
                    <span className="ov-pnl-exp-detail">
                      Purchases <span style={{ color: '#fca5a5' }}>{fmt2(totalPurchases)}</span>
                      {' + '}Unbooked <span style={{ color: '#fde68a' }}>{fmt2(totalUnbooked)}</span>
                    </span>
                  )}
                </div>
                <span className="ov-pnl-amt-exp">{fmt2(totalPurchases + totalUnbooked)}</span>
              </div>
              <div className="ov-pnl-bar-26-flex">
                <div style={{
                  height: '100%', width: `${purchPct}%`,
                  background: 'linear-gradient(90deg, #dc2626, #f87171)',
                  transition: 'width 0.8s cubic-bezier(0.4,0,0.2,1)',
                  display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                  paddingRight: totalUnbooked > 0 ? 3 : 8,
                }}>
                  {purchPct > 14 && <span style={{ fontSize: 11, fontWeight: 700, color: '#fff1f2' }}>{purchPct.toFixed(0)}%</span>}
                </div>
                {totalUnbooked > 0 && unbookedPct > 0 && (
                  <div style={{
                    height: '100%', width: `${unbookedPct}%`,
                    background: 'linear-gradient(90deg, #d97706, #fbbf24)',
                    transition: 'width 0.8s cubic-bezier(0.4,0,0.2,1)',
                    display: 'flex', alignItems: 'center', paddingLeft: 4,
                  }}>
                    {unbookedPct > 6 && <span style={{ fontSize: 10, fontWeight: 700, color: '#451a03' }}>+{unbookedPct.toFixed(0)}%</span>}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* P&L waterfall table */}
          <div className="ov-pnl-table">
            {waterfall.map((row, i) => (
              <div key={row.label}>
                {row.separator && i > 0 && <div className="ov-pnl-divider" />}
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: row.final ? '12px 16px' : '9px 16px',
                  background: row.final
                    ? (profitable ? 'rgba(52,211,153,0.07)' : 'rgba(248,113,113,0.07)')
                    : (i % 2 === 0 ? 'var(--bg2)' : 'transparent'),
                  borderBottom: i < waterfall.length - 1 ? '1px solid var(--border)' : 'none',
                }}>
                  <div className="ov-pnl-row-left">
                    <div className="ov-pnl-dot" style={{ background: row.color, opacity: row.deduction ? 0.7 : 1 }} />
                    <div>
                      <div style={{
                        fontSize: row.final ? 13 : 12,
                        fontWeight: row.final || row.separator ? 700 : 400,
                        color: row.final ? row.color : row.deduction ? 'var(--text2)' : 'var(--text)',
                        paddingLeft: row.deduction ? 8 : 0,
                      }}>
                        {row.label}
                      </div>
                      {row.sub && (
                        <div style={{ fontSize: 11, color: 'var(--text3)', paddingLeft: row.deduction ? 8 : 0 }}>{row.sub}</div>
                      )}
                    </div>
                  </div>
                  <span style={{
                    fontSize: row.final ? 15 : 13,
                    fontWeight: row.final ? 800 : row.separator ? 700 : 500,
                    fontFamily: 'var(--mono)',
                    color: row.value < 0 ? '#f87171' : row.color,
                    fontVariantNumeric: 'tabular-nums',
                  }}>
                    {row.value < 0 ? `(${fmt2(Math.abs(row.value))})` : fmt2(row.value)}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className="ov-pnl-disclaimer">
            * Estimated P&L: Sales/Purchase Registers for revenue and cost of goods, Indirect Income/Expense ledger balances for overheads — matching Tally's own P&L structure. May differ slightly from Tally where expense ledgers sit under custom sub-groups not yet recognised.
          </div>
        </div>
      )}
    </Card>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function Overview({ sourceId, syncRunId, userRole }) {
  console.log('[Overview] sourceId:', sourceId, 'syncRunId:', syncRunId);

  const { data: syncData, loading: sl, refetch: refetchSyncHistory } = useApi(() => api.syncHistory(sourceId), [sourceId]);
  const latest = syncData?.runs?.[0];
  const effectiveSyncRunId = syncRunId || latest?.sync_id || latest?.id || null;

  // When viewing the latest run (no explicit syncRunId pinned), a completed
  // background sync should surface here without the user having to navigate
  // away and back. Refetching sync history picks up the new latest run,
  // which cascades to every other useApi call below via effectiveSyncRunId.
  useOnSyncComplete(() => refetchSyncHistory());

  const [auditStatus, setAuditStatus] = React.useState('synced');
  React.useEffect(() => {
    if (!effectiveSyncRunId) { setAuditStatus('synced'); return; }
    fetch(`${BASE_URL}/audit-comments/status?syncRunId=${effectiveSyncRunId}`, { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(d => setAuditStatus(d?.status || 'synced'))
      .catch(() => setAuditStatus('synced'));
  }, [effectiveSyncRunId]);

  const { data: rec,      loading: rl  } = useApi(() => api.receivables(sourceId, effectiveSyncRunId),     [sourceId, effectiveSyncRunId]);
  const { data: stat,     loading: stl } = useApi(() => api.statutory(sourceId, effectiveSyncRunId),        [sourceId, effectiveSyncRunId]);
  const { data: pay,      loading: pl  } = useApi(() => api.outstandingPay(sourceId, effectiveSyncRunId),   [sourceId, effectiveSyncRunId]);
  const { data: unbooked, loading: ul  } = useApi(() => api.unbookedPayments(sourceId, effectiveSyncRunId), [sourceId, effectiveSyncRunId]);
  const { data: balChk,   loading: bcl } = useApi(() => api.balanceCheck(sourceId, effectiveSyncRunId),     [sourceId, effectiveSyncRunId]);
  const { data: tb,       loading: tbl } = useApi(() => api.trialBalance(sourceId, effectiveSyncRunId),     [sourceId, effectiveSyncRunId]);
  const { data: salesArr, loading: sarl} = useApi(() => api.salesRegister(sourceId, effectiveSyncRunId),    [sourceId, effectiveSyncRunId]);
  const { data: purchArr, loading: purl} = useApi(() => api.purchaseRegister(sourceId, effectiveSyncRunId), [sourceId, effectiveSyncRunId]);
  const { data: gstArr,   loading: gstl} = useApi(() => api.gstInvoices(sourceId, effectiveSyncRunId),      [sourceId, effectiveSyncRunId]);
  const { data: bankData, loading: bkl } = useApi(() => api.bankBook(sourceId, effectiveSyncRunId),         [sourceId, effectiveSyncRunId]);

  // ── Derived values ────────────────────────────────────────────────────────

  const topDebtors       = (rec?.debtors || []).slice(0, 8);
  const totalReceivables = rec?.totalOutstanding ?? 0;

  const topCreditors = (pay?.creditors || [])
    .slice()
    .sort((a, b) => parseFloat(b.total_outstanding ?? 0) - parseFloat(a.total_outstanding ?? 0))
    .slice(0, 8);
  const totalPayables = pay?.totalOutstanding ?? 0;

  const statFindings = (stat?.findings || [])
    .slice()
    .sort((a, b) => Math.abs(parseFloat(b.balance ?? 0)) - Math.abs(parseFloat(a.balance ?? 0)));
  const totalStatutoryDues = statFindings.reduce((s, f) => s + Math.abs(parseFloat(f.balance ?? 0)), 0);

  // Real Indirect Expense/Income totals from ledger closing balances (Trial
  // Balance), for the Estimated Profit calc — matches Tally's own P&L
  // structure instead of misusing Unbooked Payments/Statutory Dues as if
  // they were expenses. Ledger balances are signed Dr-negative/Cr-positive,
  // so indirect_expense sums come back negative — flip to a positive
  // deduction amount; indirect_income sums are already positive.
  const tbLedgers = tb?.ledgers || [];
  const indirectExpense = -tbLedgers
    .filter(l => l.canonicalGroup === 'indirect_expense')
    .reduce((s, l) => s + parseFloat(l.balance || 0), 0);
  const indirectIncome = tbLedgers
    .filter(l => l.canonicalGroup === 'indirect_income')
    .reduce((s, l) => s + parseFloat(l.balance || 0), 0);

  const totalUnbooked = unbooked?.totalAmount ?? 0;
  const unbookedCount = unbooked?.count       ?? 0;
  const topUnbooked = Object.values(
    (unbooked?.payments || []).reduce((acc, r) => {
      const key = r.party || '—';
      if (!acc[key]) acc[key] = { party: key, total: 0, payments: 0, account: r.payment_account || '—' };
      acc[key].total    += parseFloat(r.amount || 0);
      acc[key].payments += 1;
      return acc;
    }, {})
  ).sort((a, b) => b.total - a.total).slice(0, 8);

  const salesRows     = salesArr || [];
  const totalSales    = salesRows.reduce((s, r) => s + parseFloat(r.invoice_amount || 0), 0);
  const totalSalesTax = salesRows.reduce((s, r) => s + parseFloat(r.gst_total || 0), 0);
  const topSales = Object.values(
    salesRows.reduce((acc, r) => {
      const key = r.raw_party || '—';
      if (!acc[key]) acc[key] = { party: key, total: 0, gst: 0, invoices: 0 };
      acc[key].total    += parseFloat(r.invoice_amount || 0);
      acc[key].gst      += parseFloat(r.gst_total      || 0);
      acc[key].invoices += 1;
      return acc;
    }, {})
  ).sort((a, b) => b.total - a.total).slice(0, 5);

  const purchRows      = purchArr || [];
  const totalPurchases = purchRows.reduce((s, r) => s + parseFloat(r.invoice_amount || 0), 0);
  const totalITC       = purchRows.reduce((s, r) => s + parseFloat(r.gst_total || 0), 0);
  const topPurchases = Object.values(
    purchRows.reduce((acc, r) => {
      const key = r.raw_party || '—';
      if (!acc[key]) acc[key] = { party: key, total: 0, gst: 0, invoices: 0, gstin: r.party_gstin || null };
      acc[key].total    += parseFloat(r.invoice_amount || 0);
      acc[key].gst      += parseFloat(r.gst_total      || 0);
      acc[key].invoices += 1;
      if (!acc[key].gstin && r.party_gstin) acc[key].gstin = r.party_gstin;
      return acc;
    }, {})
  ).sort((a, b) => b.total - a.total).slice(0, 5);

  const gstRows      = gstArr || [];
  const totalTaxable = gstRows.reduce((s, r) => s + parseFloat(r.taxable || 0), 0);
  const totalGST     = gstRows.reduce((s, r) => s + parseFloat(r.gst_total || 0), 0);
  const totalCGST    = gstRows.reduce((s, r) => s + parseFloat(r.cgst || r.gst_cgst || 0), 0);
  const totalSGST    = gstRows.reduce((s, r) => s + parseFloat(r.sgst || r.gst_sgst || 0), 0);
  const totalIGST    = gstRows.reduce((s, r) => s + parseFloat(r.igst || r.gst_igst || 0), 0);
  const topGST       = gstRows
    .slice()
    .sort((a, b) => parseFloat(b.gst_total ?? b.total ?? 0) - parseFloat(a.gst_total ?? a.total ?? 0))
    .slice(0, 5);

  const bankAccounts = bankData?.accounts    || [];
  const bankCredits  = bankData?.totalCredits ?? 0;
  const bankDebits   = bankData?.totalDebits  ?? 0;
  const netMovement  = bankData?.netMovement  ?? 0;

  return (
    <PageWrap>
      <PageHeader
        title="Dashboard"
        sub={latest
          ? `Last synced: ${fmtDate(latest.synced_at)} · Period: ${latest.period_from} – ${latest.period_to}`
          : 'Select a source to load data'}
      />

      <SalesVsExpenditure
        totalSales={totalSales}
        totalSalesTax={totalSalesTax}
        totalPurchases={totalPurchases}
        totalITC={totalITC}
        totalUnbooked={totalUnbooked}
        indirectExpense={indirectExpense}
        indirectIncome={indirectIncome}
        salesRowsLen={salesRows.length}
        loading={sarl || purl || ul || tbl}
      />

      <MonthlyChart salesRows={salesRows} purchRows={purchRows} loading={sarl || purl} />

      <SectionLabel>Sales &amp; Purchase Summary</SectionLabel>
      <TwoCol>
        <Card>
          <CardHeader
            title="Top 5 customers"
            right={
              <div className="ov-header-pills">
                <TotalPill label="Invoices" value={salesRows.length.toLocaleString('en-IN')} />
                <TotalPill label="Total" value={`₹${fmt(totalSales)}`} color="var(--green)" />
              </div>
            }
          />
          {sarl ? <Skeleton rows={4} /> : (
            <>
              <Table
                columns={[
                  { label: '#', key: '_idx', render: (r, i) => <span className="ov-idx">{i + 1}</span> },
                  { label: 'Customer', key: 'party', render: r => <span className="ov-name">{r.party}</span> },
                  { label: 'Invoices', key: 'invoices', right: true, render: r => <span className="ov-cell-secondary">{r.invoices}</span> },
                  { label: 'GST (₹)', key: 'gst', right: true, mono: true,
                    render: r => <span className="ov-mono-amber">₹{fmt(r.gst)}</span> },
                  { label: 'Total (₹)', key: 'total', right: true, mono: true,
                    render: r => <span className="ov-mono-green ov-bold">₹{fmt(r.total)}</span> },
                ]}
                rows={topSales.map((r, i) => ({ ...r, _idx: i + 1 }))}
                emptyMsg="No sales data"
              />
              {salesRows.length > 0 && (
                <div className="ov-card-footer">
                  <span>Total GST collected</span>
                  <span className="ov-footer-val-amber">₹{fmt(totalSalesTax)}</span>
                </div>
              )}
            </>
          )}
        </Card>

        <Card>
          <CardHeader
            title="Top 5 vendors"
            right={
              <div className="ov-header-pills">
                <TotalPill label="Invoices" value={purchRows.length.toLocaleString('en-IN')} />
                <TotalPill label="Total" value={`₹${fmt(totalPurchases)}`} color="var(--amber)" />
              </div>
            }
          />
          {purl ? <Skeleton rows={4} /> : (
            <>
              <Table
                columns={[
                  { label: '#', key: '_idx', render: (r, i) => <span className="ov-idx">{i + 1}</span> },
                  { label: 'Vendor', key: 'party',
                    render: r => (
                      <div>
                        <div className="ov-name">{r.party}</div>
                        {r.gstin
                          ? <div className="ov-sub-info">{r.gstin}</div>
                          : <Badge text="No GSTIN" variant="amber" />}
                      </div>
                    )},
                  { label: 'Bills', key: 'invoices', right: true, render: r => <span className="ov-cell-secondary">{r.invoices}</span> },
                  { label: 'ITC (₹)', key: 'gst', right: true, mono: true,
                    render: r => <span className="ov-mono-green">₹{fmt(r.gst)}</span> },
                  { label: 'Total (₹)', key: 'total', right: true, mono: true,
                    render: r => <span className="ov-mono-amber ov-bold">₹{fmt(r.total)}</span> },
                ]}
                rows={topPurchases.map((r, i) => ({ ...r, _idx: i + 1 }))}
                emptyMsg="No purchase data"
              />
              {purchRows.length > 0 && (
                <div className="ov-card-footer">
                  <span>Total ITC available</span>
                  <span className="ov-footer-val-green">₹{fmt(totalITC)}</span>
                </div>
              )}
            </>
          )}
        </Card>
      </TwoCol>

      <SectionLabel>Receivables &amp; Payables</SectionLabel>
      <TwoCol>
        <Card>
          <CardHeader
            title="Top overdue debtors"
            right={<TotalPill label="Total" value={`₹${fmt(totalReceivables)}`} color="var(--green)" />}
          />
          {rl ? <Skeleton /> : (
            <Table
              columns={[
                { label: 'Party', key: 'name' },
                { label: '> 90d (₹)', key: 'above_90', right: true, mono: true,
                  render: r => parseFloat(r.above_90) > 0
                    ? <span className="ov-mono-red">{`₹${fmt(r.above_90)}`}</span>
                    : <span className="ov-text-dim">—</span> },
                { label: 'Total (₹)', key: 'total_outstanding', right: true, mono: true,
                  render: r => <span className="ov-mono-green ov-bold">₹{fmt(r.total_outstanding)}</span> },
              ]}
              rows={topDebtors}
              emptyMsg="No outstanding debtors"
            />
          )}
        </Card>

        <Card>
          <CardHeader
            title="Top overdue creditors"
            right={<TotalPill label="Total" value={`₹${fmt(totalPayables)}`} color="var(--red)" />}
          />
          {pl ? <Skeleton /> : (
            <Table
              columns={[
                { label: 'Party', key: 'name' },
                { label: '> 90d (₹)', key: 'above_90', right: true, mono: true,
                  render: r => parseFloat(r.above_90) > 0
                    ? <span className="ov-mono-red">{`₹${fmt(r.above_90)}`}</span>
                    : <span className="ov-text-dim">—</span> },
                { label: 'Total (₹)', key: 'total_outstanding', right: true, mono: true,
                  render: r => <span className="ov-mono-red ov-bold">₹{fmt(r.total_outstanding)}</span> },
              ]}
              rows={topCreditors}
              emptyMsg="No outstanding creditors"
            />
          )}
        </Card>
      </TwoCol>

      <SectionLabel>Unbooked Payments &amp; Statutory</SectionLabel>
      <TwoCol>
        <Card>
          <CardHeader
            title="Top parties — unbooked payments"
            right={
              <div className="ov-header-pills">
                <TotalPill label="Payments" value={unbookedCount} color="var(--amber)" />
                <TotalPill label="Amount" value={`₹${fmt(totalUnbooked)}`} color="var(--red)" />
              </div>
            }
          />
          {ul ? <Skeleton rows={4} /> : (
            <Table
              columns={[
                { label: '#', key: '_idx', render: (r, i) => <span className="ov-idx">{i + 1}</span> },
                { label: 'Party', key: 'party', render: r => <span className="ov-name">{r.party}</span> },
                { label: 'Payments', key: 'payments', right: true, render: r => <span className="ov-cell-secondary">{r.payments}</span> },
                { label: 'Total (₹)', key: 'total', right: true, mono: true,
                  render: r => <span className="ov-mono-red ov-bold">₹{fmt(r.total)}</span> },
              ]}
              rows={topUnbooked.map((r, i) => ({ ...r, _idx: i + 1 }))}
              emptyMsg="No unbooked payments found"
            />
          )}
        </Card>

        <Card>
          <CardHeader title="Statutory liabilities" />
          {stl ? <Skeleton /> : (
            <Table
              columns={[
                { label: 'Category', key: 'message',
                  render: r => <strong>{extractCategory(r.message, (r.ledger_name || '').split(' ')[0])}</strong> },
                { label: 'Ledger', key: 'ledger_name',
                  render: r => <span className="ov-cell-secondary">{r.ledger_name || '—'}</span> },
                { label: 'Balance', key: 'balance', right: true, mono: true,
                  render: r => { const b = Math.abs(parseFloat(r.balance ?? 0)); return <span style={{ color: b > 0.01 ? 'var(--red)' : 'var(--text2)' }}>₹{fmt(b)}</span>; } },
                { label: 'Status', key: 'balance',
                  render: r => { const out = Math.abs(parseFloat(r.balance ?? 0)) > 0.01; return <Badge text={out ? 'OUTSTANDING' : 'CLEAR'} variant={out ? 'red' : 'green'} />; } },
              ]}
              rows={statFindings}
              emptyMsg="No statutory ledgers found"
            />
          )}
        </Card>
      </TwoCol>

      <SectionLabel>Books Integrity</SectionLabel>
      <TwoCol>
        <BalanceCheckCard data={balChk} loading={bcl} />
        <TrialBalanceCard data={tb}     loading={tbl} />
      </TwoCol>

      <SectionLabel>GST Summary</SectionLabel>
      <Card className="ov-card-mb">
        <CardHeader
          title="GST Invoice Summary"
          right={
            <div className="ov-header-pills">
              <TotalPill label="Invoices" value={gstRows.length.toLocaleString('en-IN')} />
              <TotalPill label="Taxable" value={`₹${fmt(totalTaxable)}`} />
              <TotalPill label="Total GST" value={`₹${fmt(totalGST)}`} color="#7c3aed" />
            </div>
          }
        />
        {gstl ? <Skeleton rows={3} /> : gstRows.length === 0 ? (
          <div className="ov-empty-msg">No GST invoices found.</div>
        ) : (
          <>
            <div className="ov-stat-grid-4">
              {[
                ['Taxable Value', `₹${fmt(totalTaxable)}`, 'var(--text)'],
                ['CGST',         `₹${fmt(totalCGST)}`,    '#7c3aed'],
                ['SGST',         `₹${fmt(totalSGST)}`,    '#7c3aed'],
                ['IGST',         `₹${fmt(totalIGST)}`,    'var(--accent)'],
              ].map(([label, value, color]) => (
                <div key={label} className="ov-stat-cell">
                  <div className="ov-stat-label">{label}</div>
                  <div className="ov-stat-value-lg" style={{ color }}>{value}</div>
                </div>
              ))}
            </div>
            <div className="ov-sublabel">Top invoices by GST amount</div>
            <Table
              columns={[
                { label: 'Date', key: 'date', noWrap: true,
                  render: r => <span className="ov-text-secondary">{fmtDate(r.date)}</span> },
                { label: 'Party', key: 'party',
                  render: r => (
                    <div>
                      <div className="ov-name">{r.party ?? r.raw_party ?? '—'}</div>
                      <div className="ov-sub-info">{r.invoiceNumber ?? r.invoice_number ?? r.voucher_number}</div>
                    </div>
                  )},
                { label: 'Taxable (₹)', key: 'taxable', right: true, mono: true,
                  render: r => <span className="ov-mono-sm">₹{fmt(r.taxable)}</span> },
                { label: 'CGST (₹)', key: 'cgst', right: true, mono: true,
                  render: r => parseFloat(r.cgst ?? r.gst_cgst) > 0
                    ? <span className="ov-mono-sm">₹{fmt(r.cgst ?? r.gst_cgst)}</span>
                    : <span className="ov-text-dim">—</span> },
                { label: 'SGST (₹)', key: 'sgst', right: true, mono: true,
                  render: r => parseFloat(r.sgst ?? r.gst_sgst) > 0
                    ? <span className="ov-mono-sm">₹{fmt(r.sgst ?? r.gst_sgst)}</span>
                    : <span className="ov-text-dim">—</span> },
                { label: 'IGST (₹)', key: 'igst', right: true, mono: true,
                  render: r => parseFloat(r.igst ?? r.gst_igst) > 0
                    ? <span className="ov-mono-accent">₹{fmt(r.igst ?? r.gst_igst)}</span>
                    : <span className="ov-text-dim">—</span> },
                { label: 'Total GST (₹)', key: 'gst_total', right: true, mono: true,
                  render: r => <strong className="ov-mono-purple">₹{fmt(r.total ?? r.gst_total)}</strong> },
              ]}
              rows={topGST}
              emptyMsg="No GST invoices found"
            />
          </>
        )}
      </Card>

      <SectionLabel>Net Bank Movement</SectionLabel>
      <Card className="ov-card-mb">
        <CardHeader
          title="Bank Book Summary"
          right={
            bankData != null
              ? <Badge
                  text={`Net: ${netMovement >= 0 ? '+' : '-'}₹${fmt(Math.abs(netMovement))}`}
                  variant={netMovement >= 0 ? 'green' : 'red'}
                />
              : null
          }
        />
        {bkl ? <Skeleton rows={4} /> : !bankData ? (
          <div className="ov-empty-msg">No bank data found.</div>
        ) : (
          <>
            <div className="ov-stat-grid-3">
              {[
                ['Total Credits (Inflow)', `₹${fmt(bankCredits)}`, 'var(--green)'],
                ['Total Debits (Outflow)', `₹${fmt(bankDebits)}`,  'var(--red)'],
                ['Net Movement',
                  `${netMovement >= 0 ? '+' : '-'}₹${fmt(Math.abs(netMovement))}`,
                  netMovement >= 0 ? 'var(--green)' : 'var(--red)'],
              ].map(([label, value, color]) => (
                <div key={label} className="ov-stat-cell">
                  <div className="ov-stat-label">{label}</div>
                  <div className="ov-stat-value-lg" style={{ color }}>{value}</div>
                </div>
              ))}
            </div>
            {bankAccounts.length > 0 && (
              <>
                <div className="ov-sublabel">Per account</div>
                <Table
                  columns={[
                    { label: 'Bank Account', key: 'account', render: r => <span className="ov-name">{r.account ?? '—'}</span> },
                    { label: 'Transactions', key: 'transaction_count', right: true, render: r => <span className="ov-cell-secondary">{r.transaction_count ?? 0}</span> },
                    { label: 'Credits (₹)', key: 'total_credits', right: true, mono: true,
                      render: r => <span className="ov-mono-green">₹{fmt(r.total_credits ?? 0)}</span> },
                    { label: 'Debits (₹)', key: 'total_debits', right: true, mono: true,
                      render: r => <span className="ov-mono-red">₹{fmt(r.total_debits ?? 0)}</span> },
                    { label: 'Net (₹)', key: '_net', right: true, mono: true,
                      render: r => {
                        const net = parseFloat(r.total_credits ?? 0) - parseFloat(r.total_debits ?? 0);
                        return (
                          <span className={`${net >= 0 ? 'ov-mono-green' : 'ov-mono-red'} ov-bold-700`}>
                            {net >= 0 ? '+' : '-'}₹{fmt(Math.abs(net))}
                          </span>
                        );
                      }},
                  ]}
                  rows={bankAccounts}
                  emptyMsg="No bank accounts found"
                />
              </>
            )}
          </>
        )}
      </Card>

      <SectionLabel>Latest Sync</SectionLabel>
      <Card>
        <CardHeader title="Latest sync" />
        {sl ? <Skeleton rows={4} /> : !latest ? (
          <div className="ov-empty-msg">No sync found — run POST /sync first</div>
        ) : (
          <div className="ov-sync-body">
            {[
              ['Sync Status',  <Badge text={latest.status} variant={latest.status === 'complete' ? 'green' : latest.status === 'failed' ? 'red' : 'amber'} />],
              ['Audit Status', <AuditStatusBadge status={auditStatus} />],
              ['Source',       latest.source_name ?? '—'],
              ['Period',       `${latest.period_from} – ${latest.period_to}`],
              ['Vouchers',     (latest.voucher_count ?? 0).toLocaleString('en-IN')],
              ['Issues found', (latest.issue_count ?? 0).toLocaleString('en-IN')],
              ['Synced at',    fmtDate(latest.synced_at)],
            ].map(([label, value]) => (
              <div key={label} className="ov-sync-row">
                <span className="ov-sync-label">{label}</span>
                <span className="ov-sync-value">{value}</span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </PageWrap>
  );
}
